import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Button } from '@/app/components/ui/button'
import { FileText, FileSpreadsheet, FileIcon, Download, Loader2 } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select'
import { Input } from './input'
import { CatalogSelect } from './CatalogSelect'

export type ExportFormat = 'pdf' | 'excel' | 'word'

export interface ExportFilters {
  limitType: 'all' | 'custom'
  customLimit?: string
  created_from?: string
  created_to?: string
  updated_from?: string
  updated_to?: string
  category_id?: string
  department_id?: string
  product_type_id?: string
}

interface ExportModalProps {
  isOpen: boolean
  onClose: () => void
  onExport: (format: ExportFormat, filters?: ExportFilters) => void
  allowedFormats?: ExportFormat[]
  title?: string
  isProductExport?: boolean
  isExporting?: boolean
}

const formatConfig = {
  pdf: {
    label: 'Documento PDF (.pdf)',
    icon: FileText,
    color: 'text-red-500',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200 hover:border-red-500',
    ringColor: 'ring-red-500'
  },
  excel: {
    label: 'Hoja de Cálculo Excel (.xlsx)',
    icon: FileSpreadsheet,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200 hover:border-green-500',
    ringColor: 'ring-green-500'
  },
  word: {
    label: 'Documento Word (.docx)',
    icon: FileIcon,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200 hover:border-blue-500',
    ringColor: 'ring-blue-500'
  }
}

export function ExportModal({
  isOpen,
  onClose,
  onExport,
  allowedFormats = ['pdf', 'excel', 'word'],
  title = 'Exportar Información',
  isProductExport = false,
  isExporting = false
}: ExportModalProps) {
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>(allowedFormats[0] || 'pdf')

  const [filters, setFilters] = useState<ExportFilters>({
    limitType: 'all',
    customLimit: '',
    created_from: '',
    created_to: '',
    updated_from: '',
    updated_to: '',
    category_id: '',
    department_id: '',
  })

  React.useEffect(() => {
    if (isOpen) {
      setFilters({
        limitType: 'all',
        customLimit: '',
        created_from: '',
        created_to: '',
        updated_from: '',
        updated_to: '',
        category_id: '',
        department_id: '',
        product_type_id: '',
      })
      if (!allowedFormats.includes(selectedFormat)) {
        setSelectedFormat(allowedFormats[0] || 'pdf')
      }
    }
  }, [isOpen, allowedFormats])

  const handleExport = () => {
    onExport(selectedFormat, isProductExport ? filters : undefined)
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] p-6">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Selecciona el formato en el que deseas exportar este documento.
          </DialogDescription>
        </DialogHeader>

        {isProductExport && (
          <div className="grid gap-6 py-4 border-b border-slate-100 mb-2">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Cantidad de registros</label>
                <Select
                  value={filters.limitType}
                  onValueChange={(val: 'all' | 'custom') => setFilters({ ...filters, limitType: val })}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="custom">Personalizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {filters.limitType === 'custom' && (
                <div>
                  <label className="text-xs font-medium text-slate-700 mb-1 block">Número de registros</label>
                  <Input
                    type="number"
                    min="1"
                    className="h-9"
                    value={filters.customLimit}
                    onChange={(e) => setFilters({ ...filters, customLimit: e.target.value })}
                    placeholder="Ej. 1000"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Fecha de creación (Desde)</label>
                <Input
                  type="date"
                  className="h-9 text-sm"
                  value={filters.created_from}
                  onChange={(e) => setFilters({ ...filters, created_from: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Fecha de creación (Hasta)</label>
                <Input
                  type="date"
                  className="h-9 text-sm"
                  value={filters.created_to}
                  onChange={(e) => setFilters({ ...filters, created_to: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Fecha de act. (Desde)</label>
                <Input
                  type="date"
                  className="h-9 text-sm"
                  value={filters.updated_from}
                  onChange={(e) => setFilters({ ...filters, updated_from: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Fecha de act. (Hasta)</label>
                <Input
                  type="date"
                  className="h-9 text-sm"
                  value={filters.updated_to}
                  onChange={(e) => setFilters({ ...filters, updated_to: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Categoría</label>
                <CatalogSelect
                  catalog="categories"
                  placeholder="Todas"
                  value={filters.category_id}
                  onChange={(val: string) => setFilters({ ...filters, category_id: val })}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Departamento</label>
                <CatalogSelect
                  catalog="departments"
                  placeholder="Todos"
                  value={filters.department_id}
                  onChange={(val: string) => setFilters({ ...filters, department_id: val })}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Tipo de producto</label>
                <CatalogSelect
                  catalog="product_types"
                  placeholder="Todos"
                  value={filters.product_type_id}
                  onChange={(val: string) => setFilters({ ...filters, product_type_id: val })}
                />
              </div>
            </div>
          </div>
        )}

        <div className="grid gap-3 py-2">
          {allowedFormats.map((format) => {
            const config = formatConfig[format]
            const isSelected = selectedFormat === format
            const Icon = config.icon

            return (
              <button
                key={format}
                type="button"
                onClick={() => setSelectedFormat(format)}
                className={`flex items-center gap-4 rounded-xl border-2 p-3.5 text-left transition-all ${isSelected
                    ? `${config.borderColor} ${config.bgColor} shadow-sm`
                    : 'border-slate-200 bg-white hover:bg-slate-50 cursor-pointer'
                  }`}
              >
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${isSelected ? 'bg-white shadow-sm' : config.bgColor}`}>
                  <Icon className={`h-5 w-5 ${config.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-slate-900">
                    {config.label}
                  </h4>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">
                    {format === 'pdf' && 'Ideal para imprimir o enviar'}
                    {format === 'excel' && 'Ideal para análisis de datos'}
                    {format === 'word' && 'Ideal para editar el contenido'}
                  </p>
                </div>
                <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${isSelected
                    ? format === 'pdf' ? 'border-red-500 bg-red-500'
                      : format === 'excel' ? 'border-green-500 bg-green-500'
                        : 'border-blue-500 bg-blue-500'
                    : 'border-slate-300'
                  }`}>
                  {isSelected && <div className="h-2 w-2 rounded-full bg-white" />}
                </div>
              </button>
            )
          })}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isExporting}>
            Cancelar
          </Button>
          <Button onClick={handleExport} disabled={isExporting} className="gap-2">
            {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {isExporting ? 'Exportando...' : 'Exportar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
